import { useMemo, useState,useEffect } from "react";
import { api, ROUTES, imgUrl, toFormData } from "./api";
import {
  useAdmin,
  useAsync,
  Modal,
  Field,
  ImageInput,
  Pager,
  ErrorState,
  Loading,
  Empty,
  PageHead,
  money,
} from "./ui";

const PER = 10;

export default function Products() {
  const { toast, confirm } = useAdmin();
  const { data, loading, error, reload } = useAsync(() =>
    Promise.all([api.get(ROUTES.products), api.get(ROUTES.categories)]),
  );
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [stock, setStock] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // null | {} (new) | product

  const [products, categories] = data || [[], []];
  const catName = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.name])),
    [categories],
  );

  const filtered = useMemo(
    () =>
      products.filter((p) => {
        if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
        if (cat && String(p.category_id) !== cat) return false;
        if (stock === "low" && !(p.stock > 0 && p.stock <= 5)) return false;
        if (stock === "out" && p.stock !== 0) return false;
        return true;
      }),
    [products, q, cat, stock],
  );
  const rows = filtered.slice((page - 1) * PER, page * PER);

  const remove = async (p) => {
    const ok = await confirm({
      title: "Delete product",
      body: `Delete "${p.name}"? This cannot be undone. Products that appear in orders, carts or reviews may be blocked by the database.`,
    });
    if (!ok) return;
    try {
      await api.del(`${ROUTES.products}/deleteproduct/${p.id}`);
      toast("Product deleted");
      reload();
    } catch (e) {
      toast(e.message, "err");
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;

 

  return (
    <>
      <PageHead
        title="Products"
        sub={`${products.length} in your catalog`}
        actions={
          <button className="btn primary" onClick={() => setEditing({})}>
            Add product
          </button>
        }
      />

      <div className="toolbar">
        <input
          className="input"
          placeholder="Search by name"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input"
          value={cat}
          onChange={(e) => {
            setCat(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className="input"
          value={stock}
          onChange={(e) => {
            setStock(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Any stock level</option>
          <option value="low">Low (1-5)</option>
          <option value="out">Out of stock</option>
        </select>
      </div>

      {loading ? (
        <Loading />
      ) : filtered.length === 0 ? (
        <Empty
          title={
            products.length
              ? "No products match these filters"
              : "No products yet"
          }
          body={
            products.length
              ? "Clear a filter to see more."
              : "Add your first product to start selling."
          }
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Tags</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="cell-media">
                      {p.img ? (
                        <img className="thumb" src={imgUrl(p.img)} alt="" />
                      ) : (
                        <span className="thumb" />
                      )}
                      <span>{p.name}</span>
                    </div>
                  </td>
                  <td>{catName[p.category_id] || "-"}</td>
                  <td>
                    {money(p.price)}
                    {p.priceMax ? ` - ${money(p.priceMax)}` : ""}
                  </td>
                  <td>
                    {p.stock === 0 ? (
                      <span className="badge danger">Out of stock</span>
                    ) : p.stock <= 5 ? (
                      <span className="badge warn">{p.stock} left</span>
                    ) : (
                      p.stock
                    )}
                  </td>
                  <td>
                    {p.is_featured ? (
                      <span className="badge info">Featured</span>
                    ) : null}{" "}
                    {p.is_best_seller ? (
                      <span className="badge ok">Best seller</span>
                    ) : null}
                  </td>
                  <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 8,
                      }}
                    >
                      <button className="btn sm" onClick={() => setEditing(p)}>
                        Edit
                      </button>
                      <button
                        className="btn sm danger-ghost"
                        onClick={() => remove(p)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={page} total={filtered.length} per={PER} onPage={setPage} />

      {editing && (
        <ProductForm
          product={editing.id ? editing : null}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function ProductForm({ product, categories, onClose, onSaved }) {
  const { toast } = useAdmin();
  const [f, setF] = useState({
  name: product?.name ?? "",
  category_id: product?.category_id ?? "",
  price: product?.price ?? "",
  priceMax: product?.priceMax ?? "",
  stock: product?.stock ?? "",
  description: product?.description ?? "",
  Benefits: product?.Benefits ?? "",
  Ingredients: product?.Ingredients ?? "",
  isBestseller: !!product?.is_bestseller,  
});
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!f.name.trim()) er.name = "Enter a product name";
    if (f.category_id === "") er.category_id = "Choose a category";
    if (f.price === "" || Number(f.price) < 0)
      er.price = "Enter a price of 0 or more";
    if (f.priceMax !== "" && Number(f.priceMax) < Number(f.price))
      er.priceMax = "Must be at least the price";
    if (
      f.stock !== "" &&
      (!Number.isInteger(Number(f.stock)) || Number(f.stock) < 0)
    )
      er.stock = "Use a whole number";
    setErrors(er);
    if (Object.keys(er).length) return;

    setSaving(true);
    try {
     const form = toFormData({
  ...f,
  name: f.name.trim(),
  description: f.description.trim(),
  isBestseller: f.isBestseller ? "true" : "false",  
  image: file,
});
      extraFiles.forEach((f) => form.append("images", f));
      console.log("extraFiles:", extraFiles.length, [...form.entries()]);
      if (product)
        await api.upload(
          "PUT",
          `${ROUTES.products}/updateproduct/${product.id}`,
          form,
        );
      else await api.upload("POST", `${ROUTES.products}/addproduct`, form);
      toast(product ? "Product saved" : "Product added");
      onSaved();
    } catch (err) {
      toast(err.message, "err");
      setSaving(false);
    }
  };

   const [gallery, setGallery] = useState([]); 
const [extraFiles, setExtraFiles] = useState([]); // new files picked now

useEffect(() => {
  if (!product) return;
  api
    .get(`${ROUTES.products}/${product.id}`)
    .then((p) => setGallery(p.images || []))
    .catch(() => {});
}, [product]);

const removeSaved = async (g) => {
  try {
    await api.del(`${ROUTES.products}/deleteimage/${g.id}`);
    setGallery((list) => list.filter((x) => x.id !== g.id));
  } catch (err) {
    toast(err.message, "err");
  }
};

  return (
    <Modal
      wide
      title={product ? "Edit product" : "Add product"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" form="product-form" disabled={saving}>
            {saving ? "Saving…" : product ? "Save changes" : "Add product"}
          </button>
        </>
      }
    >
    
      <form
        id="product-form"
        onSubmit={submit}
        className="form-grid"
        noValidate
      >
        <Field label="Name" error={errors.name}>
          <input
            className="input"
            value={f.name}
            onChange={set("name")}
            autoFocus
          />
        </Field>
        <Field label="Category" error={errors.category_id}>
          <select
            className="input"
            value={f.category_id}
            onChange={set("category_id")}
          >
            <option value="">Choose…</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Price (Rs)" error={errors.price}>
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={f.price}
            onChange={set("price")}
          />
        </Field>
        <Field
          label="Highest price (Rs)"
          error={errors.priceMax}
          hint="Optional. Fill in to show a price range."
        >
          <input
            className="input"
            type="number"
            min="0"
            step="0.01"
            value={f.priceMax}
            onChange={set("priceMax")}
          />
        </Field>
        <Field label="Stock" error={errors.stock} hint="Leave empty for 0.">
          <input
            className="input"
            type="number"
            min="0"
            step="1"
            value={f.stock}
            onChange={set("stock")}
          />
        </Field>
        <div className="span-2">
          <Field label="Description">
            <textarea
              className="input"
              rows="4"
              value={f.description}
              onChange={set("description")}
            />
          </Field>
        </div>
        <div className="span-2">
          <Field label="Benefits">
            <textarea
              className="input"
              rows="3"
              value={f.Benefits}
              onChange={set("Benefits")}
            />
          </Field>
        </div>
        <div className="span-2">
          <Field label="Ingredients">
            <textarea
              className="input"
              rows="3"
              value={f.Ingredients}
              onChange={set("Ingredients")}
            />
          </Field>
        </div>
        <div className="span-2">
  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
    <input
      type="checkbox"
      checked={f.isBestseller}
      onChange={(e) => setF({ ...f, isBestseller: e.target.checked })}
    />
    Mark as Best Seller
  </label>
</div>
        <div className="span-2">
          <span className="field-label">Image</span>
          <ImageInput current={product?.img} file={file} onChange={setFile} />
        </div>
          <div className="span-2">
  <span className="field-label">More images</span>

  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "8px 0" }}>
    {gallery.map((g) => (
      <div key={g.id} style={{ position: "relative" }}>
        <img
          src={imgUrl(g.url)}
          alt=""
          style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6 }}
        />
        <button
          type="button"
          onClick={() => removeSaved(g)}
          style={{ position: "absolute", top: -6, right: -6 }}
        >
          ×
        </button>
      </div>
    ))}
    {extraFiles.map((f, i) => (
      <div key={i} style={{ position: "relative" }}>
        <img
          src={URL.createObjectURL(f)}
          alt=""
          style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6 }}
        />
        <button
          type="button"
          onClick={() => setExtraFiles((l) => l.filter((_, x) => x !== i))}
          style={{ position: "absolute", top: -6, right: -6 }}
        >
          ×
        </button>
      </div>
    ))}
  </div>

  <input
  type="file"
  accept="image/*"
  multiple
  onChange={(e) => {
    const picked = Array.from(e.target.files);
    setExtraFiles((l) => [...l, ...picked]);
    e.target.value = "";
  }}
/>
</div>
      </form>
    </Modal>
  );
}
